import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useDraftAutosave } from '../hooks/useDraftAutosave';
import { DraftRestoreBanner, DraftSavedIndicator } from '../components/DraftBanner';
import { useCompany } from '../contexts/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import ContactDocuments from '../components/contacts/ContactDocuments';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  CircleNotch,
  MagnifyingGlass,
  Users,
  Storefront,
  DotsThreeVertical,
  PencilSimple,
  Trash,
  EnvelopeSimple,
  Phone,
  MapPin,
  Eye,
  IdentificationCard,
  Buildings
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactTypeInfo = {
  customer: { label: 'Customer', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400', icon: Users },
  supplier: { label: 'Supplier', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400', icon: Storefront },
  both: { label: 'Both', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400', icon: Users },
};

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "IN", name: "India" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "JP", name: "Japan" },
  { code: "SG", name: "Singapore" },
  { code: "AE", name: "United Arab Emirates" },
];

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "CAD", "AUD", "SGD", "AED"];

export default function ContactsPage() {
  const { hasPermission, user } = useAuth();
  const { currency: companyCurrency, formatCurrency } = useCompany();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const typeParam = searchParams.get('type'); // 'customer' | 'supplier' | null
  const initialTab = typeParam === 'customer' ? 'customers' : typeParam === 'supplier' ? 'suppliers' : 'all';

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Sync tab when URL ?type= changes (clicking sidebar Clients vs Suppliers)
  useEffect(() => {
    const next = typeParam === 'customer' ? 'customers' : typeParam === 'supplier' ? 'suppliers' : 'all';
    setActiveTab(next);
  }, [typeParam]);

  const emptyForm = {
    name: '',
    company_name: '',
    contact_type: typeParam === 'supplier' ? 'supplier' : 'customer',
    phone: '',
    email: '',
    address: '',
    country: '',
    tax_id: '',
    currency: companyCurrency,
    notes: '',
    // Extended channels
    whatsapp: '',
    alt_phone: '',
    alt_email: '',
    website: '',
    // Bank
    bank_name: '',
    bank_account_holder: '',
    bank_account_number: '',
    bank_iban: '',
    bank_swift: '',
    bank_branch: '',
    // Indian GST
    gstin: '',
    pan: '',
    state_code: '',
    state_name: '',
    // KYC
    trade_license_number: '',
    trade_license_expiry: '',
    vat_certificate_number: '',
    vat_certificate_expiry: '',
    // Embedded projects/jobs
    projects: [],
  };
  
  const [formData, setFormData] = useState(emptyForm);

  // Auto-save draft for new contact creation (not edits)
  const contactDraft = useDraftAutosave({
    key: `contact-${formData.contact_type || 'new'}`,
    data: formData,
    enabled: isCreateOpen,
    userId: user?.id,
  });

  // Keep the form's default currency in sync once the company loads
  useEffect(() => {
    setFormData((prev) => (prev.currency ? prev : { ...prev, currency: companyCurrency }));
  }, [companyCurrency]);

  const canCreate = hasPermission('sales', 'create');
  const canEdit = hasPermission('sales', 'edit');
  const canDelete = hasPermission('sales', 'delete');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API_URL}/contacts`);
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Build the payload that goes to the API.  Company name is the primary,
  // mandatory identity field; if no contact-person is typed, mirror company
  // name into `name` so the backend (which still requires `name`) is happy
  // and the legacy list views keep working.
  const buildPayload = () => {
    const p = { ...formData };
    if (!p.name || !String(p.name).trim()) {
      p.name = p.company_name || '';
    }
    return p;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/contacts`, buildPayload());
      toast.success('Contact created successfully');
      setIsCreateOpen(false);
      contactDraft.clear();
      setFormData(emptyForm);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedContact) return;
    setFormLoading(true);
    try {
      await axios.patch(`${API_URL}/contacts/${selectedContact.id}`, buildPayload());
      toast.success('Contact updated successfully');
      setIsEditOpen(false);
      setSelectedContact(null);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API_URL}/contacts/${contactId}`);
      toast.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    }
  };

  const openEditDialog = (contact) => {
    setSelectedContact(contact);
    // Start from the empty-form template so every controlled input has a defined
    // value (avoids React "uncontrolled -> controlled" warnings), then overlay
    // ALL persisted fields from the contact — including the extended channels
    // (whatsapp, alt_*, website), Indian GST (gstin, pan, state_*), bank details,
    // and KYC. Previously this handler only copied a basic subset, which caused
    // extended fields to read back as blank on Edit.
    const overlay = {};
    Object.keys(emptyForm).forEach((k) => {
      if (contact[k] !== undefined && contact[k] !== null) {
        overlay[k] = contact[k];
      }
    });
    setFormData({
      ...emptyForm,
      ...overlay,
      contact_type: contact.contact_type || 'customer',
      currency: contact.currency || companyCurrency,
    });
    setIsEditOpen(true);
  };

  const openDetailDialog = (contact) => {
    setSelectedContact(contact);
    setIsDetailOpen(true);
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (contact.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (contact.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    // Tabs use plural state ('customers' / 'suppliers' / 'all') but contact_type
    // is singular ('customer' / 'supplier' / 'both'). Map plural -> singular here.
    const tabSingular = activeTab === 'customers' ? 'customer'
                       : activeTab === 'suppliers' ? 'supplier'
                       : activeTab;
    const matchesTab = tabSingular === 'all'
      || contact.contact_type === tabSingular
      || ((tabSingular === 'customer' || tabSingular === 'supplier') && contact.contact_type === 'both');
    return matchesSearch && matchesTab;
  });

  const renderContactForm = (onSubmit, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      {!isEdit && (
        <DraftRestoreBanner
          visible={contactDraft.hasDraft}
          savedAt={contactDraft.draftSavedAt}
          label="contact"
          onRestore={() => { const r = contactDraft.restore(); if (r) setFormData(r); }}
          onDiscard={contactDraft.discard}
        />
      )}
      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="basic" data-testid="tab-basic">Basic</TabsTrigger>
          <TabsTrigger value="tax" data-testid="tab-tax">Tax & KYC</TabsTrigger>
          <TabsTrigger value="bank" data-testid="tab-bank">Bank Details</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs" disabled={!isEdit}>
            Documents{!isEdit && <span className="ml-1 text-[9px]">(after save)</span>}
          </TabsTrigger>
        </TabsList>

        {/* ============ BASIC ============ */}
        <TabsContent value="basic" className="space-y-4 pt-4">
          {/* Auto-generated codes (visible once saved) */}
          {isEdit && selectedContact && (
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/40 p-3">
              <div className="text-xs">
                <span className="text-muted-foreground">Client Code: </span>
                <span className="font-mono font-semibold" data-testid="contact-client-code">
                  {selectedContact.client_code || '—'}
                </span>
              </div>
              <div className="text-xs">
                <span className="text-muted-foreground">Supplier Code: </span>
                <span className="font-mono font-semibold" data-testid="contact-supplier-code">
                  {selectedContact.supplier_code || '—'}
                </span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))}
                placeholder="e.g. VRV Tech Solutions LLC"
                required
                data-testid="contact-company"
              />
              <p className="text-[10px] text-muted-foreground">
                This is the name shown on invoice/quote summary lists.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Mr. John Smith"
                data-testid="contact-name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select value={formData.contact_type} onValueChange={(v) => setFormData(p => ({ ...p, contact_type: v }))}>
                <SelectTrigger data-testid="contact-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="supplier">Supplier</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={formData.currency} onValueChange={(v) => setFormData(p => ({ ...p, currency: v }))}>
                <SelectTrigger data-testid="contact-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} data-testid="contact-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} data-testid="contact-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>WhatsApp</Label>
              <Input value={formData.whatsapp} onChange={(e) => setFormData(p => ({ ...p, whatsapp: e.target.value }))} placeholder="+971..." data-testid="contact-whatsapp" />
            </div>
            <div className="space-y-2">
              <Label>Alternate Phone</Label>
              <Input value={formData.alt_phone} onChange={(e) => setFormData(p => ({ ...p, alt_phone: e.target.value }))} data-testid="contact-alt-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Alternate Email</Label>
              <Input type="email" value={formData.alt_email} onChange={(e) => setFormData(p => ({ ...p, alt_email: e.target.value }))} data-testid="contact-alt-email" />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={formData.website} onChange={(e) => setFormData(p => ({ ...p, website: e.target.value }))} placeholder="https://..." data-testid="contact-website" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Country</Label>
              <Select value={formData.country} onValueChange={(v) => setFormData(p => ({ ...p, country: v }))}>
                <SelectTrigger data-testid="contact-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} data-testid="contact-address" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="contact-notes" />
          </div>
        </TabsContent>

        {/* ============ TAX & KYC ============ */}
        <TabsContent value="tax" className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tax ID / VAT TRN</Label>
              <Input value={formData.tax_id} onChange={(e) => setFormData(p => ({ ...p, tax_id: e.target.value }))} placeholder="VAT/GST/TRN Number" data-testid="contact-taxid" />
            </div>
            <div className="space-y-2">
              <Label>GSTIN (India)</Label>
              <Input value={formData.gstin || ''} onChange={(e) => setFormData(p => ({ ...p, gstin: e.target.value }))} placeholder="22AAAAA0000A1Z5" data-testid="contact-gstin" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>PAN (India)</Label>
              <Input value={formData.pan || ''} onChange={(e) => setFormData(p => ({ ...p, pan: e.target.value }))} placeholder="AAAAA0000A" data-testid="contact-pan" />
            </div>
            <div className="space-y-2">
              <Label>State Code (India)</Label>
              <Input value={formData.state_code || ''} onChange={(e) => setFormData(p => ({ ...p, state_code: e.target.value }))} placeholder="22, 27, etc." data-testid="contact-state-code" />
            </div>
          </div>
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-semibold text-muted-foreground">KYC Documents Reference</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Trade License Number</Label>
                <Input value={formData.trade_license_number} onChange={(e) => setFormData(p => ({ ...p, trade_license_number: e.target.value }))} data-testid="contact-trade-license" />
              </div>
              <div className="space-y-2">
                <Label>Trade License Expiry</Label>
                <Input type="date" value={formData.trade_license_expiry} onChange={(e) => setFormData(p => ({ ...p, trade_license_expiry: e.target.value }))} data-testid="contact-trade-license-expiry" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>VAT Certificate Number</Label>
                <Input value={formData.vat_certificate_number} onChange={(e) => setFormData(p => ({ ...p, vat_certificate_number: e.target.value }))} data-testid="contact-vat-cert" />
              </div>
              <div className="space-y-2">
                <Label>VAT Certificate Expiry</Label>
                <Input type="date" value={formData.vat_certificate_expiry} onChange={(e) => setFormData(p => ({ ...p, vat_certificate_expiry: e.target.value }))} data-testid="contact-vat-cert-expiry" />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ============ BANK DETAILS ============ */}
        <TabsContent value="bank" className="space-y-4 pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Bank Name</Label>
              <Input value={formData.bank_name} onChange={(e) => setFormData(p => ({ ...p, bank_name: e.target.value }))} data-testid="contact-bank-name" />
            </div>
            <div className="space-y-2">
              <Label>Account Holder Name</Label>
              <Input value={formData.bank_account_holder} onChange={(e) => setFormData(p => ({ ...p, bank_account_holder: e.target.value }))} data-testid="contact-bank-holder" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Account Number</Label>
              <Input value={formData.bank_account_number} onChange={(e) => setFormData(p => ({ ...p, bank_account_number: e.target.value }))} data-testid="contact-bank-account" />
            </div>
            <div className="space-y-2">
              <Label>IBAN</Label>
              <Input value={formData.bank_iban} onChange={(e) => setFormData(p => ({ ...p, bank_iban: e.target.value }))} data-testid="contact-bank-iban" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SWIFT / BIC</Label>
              <Input value={formData.bank_swift} onChange={(e) => setFormData(p => ({ ...p, bank_swift: e.target.value }))} data-testid="contact-bank-swift" />
            </div>
            <div className="space-y-2">
              <Label>Branch</Label>
              <Input value={formData.bank_branch} onChange={(e) => setFormData(p => ({ ...p, bank_branch: e.target.value }))} data-testid="contact-bank-branch" />
            </div>
          </div>
        </TabsContent>

        {/* ============ PROJECTS ============ */}
        <TabsContent value="projects" className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Projects / Jobs</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Add the projects this contact works with. They appear as a quick-pick dropdown when you create a Quotation or Invoice for them.
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setFormData((p) => ({
                ...p,
                projects: [...(p.projects || []), { name: '', location: '', reference: '', status: 'active' }],
              }))}
              data-testid="contact-add-project"
            >
              + Add Project
            </Button>
          </div>

          {(!formData.projects || formData.projects.length === 0) ? (
            <div className="rounded-lg border-2 border-dashed border-muted-foreground/30 p-6 text-center text-sm text-muted-foreground">
              No projects yet. Click <strong>+ Add Project</strong> to add one.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" style={{ minWidth: '600px' }}>
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Project Name</th>
                    <th className="text-left px-3 py-2 font-medium w-44">Location</th>
                    <th className="text-left px-3 py-2 font-medium w-40">Reference</th>
                    <th className="text-left px-3 py-2 font-medium w-28">Status</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {formData.projects.map((proj, idx) => (
                    <tr key={idx} data-testid={`contact-project-row-${idx}`}>
                      <td className="px-2 py-1.5">
                        <Input
                          value={proj.name || ''}
                          onChange={(e) => setFormData((p) => {
                            const next = [...p.projects];
                            next[idx] = { ...next[idx], name: e.target.value };
                            return { ...p, projects: next };
                          })}
                          placeholder="e.g. Marina Tower Phase 2"
                          className="h-8 text-sm"
                          data-testid={`contact-project-name-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          value={proj.location || ''}
                          onChange={(e) => setFormData((p) => {
                            const next = [...p.projects];
                            next[idx] = { ...next[idx], location: e.target.value };
                            return { ...p, projects: next };
                          })}
                          placeholder="Dubai Marina"
                          className="h-8 text-sm"
                          data-testid={`contact-project-location-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          value={proj.reference || ''}
                          onChange={(e) => setFormData((p) => {
                            const next = [...p.projects];
                            next[idx] = { ...next[idx], reference: e.target.value };
                            return { ...p, projects: next };
                          })}
                          placeholder="MT-P2-001"
                          className="h-8 text-sm"
                          data-testid={`contact-project-ref-${idx}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={proj.status || 'active'}
                          onChange={(e) => setFormData((p) => {
                            const next = [...p.projects];
                            next[idx] = { ...next[idx], status: e.target.value };
                            return { ...p, projects: next };
                          })}
                          className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                          data-testid={`contact-project-status-${idx}`}
                        >
                          <option value="active">Active</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setFormData((p) => ({
                            ...p,
                            projects: p.projects.filter((_, i) => i !== idx),
                          }))}
                          data-testid={`contact-project-remove-${idx}`}
                          title="Remove project"
                        >
                          ✕
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* ============ DOCUMENTS ============ */}
        <TabsContent value="docs" className="space-y-4 pt-4">
          {isEdit && selectedContact ? (
            <ContactDocuments contact={selectedContact} onChange={fetchContacts} />
          ) : (
            <p className="text-sm text-muted-foreground">Save the contact first, then upload Trade License, VAT Certificate, etc.</p>
          )}
        </TabsContent>
      </Tabs>

      <DialogFooter>
        {!isEdit && <div className="mr-auto"><DraftSavedIndicator savedAt={contactDraft.lastAutoSavedAt} /></div>}
        <Button type="submit" disabled={formLoading} data-testid="contact-submit">
          {formLoading ? <CircleNotch className="animate-spin" size={18} /> : (isEdit ? 'Save Changes' : 'Create Contact')}
        </Button>
      </DialogFooter>
    </form>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="contacts-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Contacts
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Manage your customers and suppliers
          </p>
        </div>
        {canCreate && (
          <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) setFormData(emptyForm); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn">
                <Plus size={18} className="mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create New Contact</DialogTitle>
                <DialogDescription>Add a new customer or supplier</DialogDescription>
              </DialogHeader>
              {renderContactForm(handleCreate, false)}
            </DialogContent>
          </Dialog>
        )}
      </motion.div>

      {/* Search and Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
          <Input
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="contact-search"
          />
        </div>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All ({contacts.length})</TabsTrigger>
          <TabsTrigger value="customer" data-testid="tab-customer">
            Customers ({contacts.filter(c => c.contact_type === 'customer' || c.contact_type === 'both').length})
          </TabsTrigger>
          <TabsTrigger value="supplier" data-testid="tab-supplier">
            Suppliers ({contacts.filter(c => c.contact_type === 'supplier' || c.contact_type === 'both').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-tactile rounded-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Contact</th>
                    <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Type</th>
                    <th className="text-left px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Contact Info</th>
                    <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Invoices</th>
                    <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Total</th>
                    <th className="text-right px-6 py-4 text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredContacts.map((contact) => {
                    const typeInfo = contactTypeInfo[contact.contact_type];
                    return (
                      <tr key={contact.id} className="hover:bg-muted/30 transition-colors" data-testid={`contact-row-${contact.id}`}>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium">{contact.company_name || contact.name}</p>
                            {contact.company_name && contact.name && contact.name.toLowerCase() !== contact.company_name.toLowerCase() && (
                              <p className="text-sm text-muted-foreground">Attn: {contact.name}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant="outline" className={typeInfo?.color}>
                            {typeInfo?.label}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1 text-sm">
                            {contact.email && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <EnvelopeSimple size={14} />
                                {contact.email}
                              </div>
                            )}
                            {contact.phone && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Phone size={14} />
                                {contact.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">{contact.total_invoices}</td>
                        <td className="px-6 py-4 text-right font-mono">{formatCurrency(contact.total_amount)}</td>
                        <td className="px-6 py-4 text-right">
                          {(canEdit || canDelete) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`contact-actions-${contact.id}`}>
                                  <DotsThreeVertical size={18} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openDetailDialog(contact)}>
                                  <Eye size={16} className="mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {canEdit && (
                                  <DropdownMenuItem onClick={() => openEditDialog(contact)}>
                                    <PencilSimple size={16} className="mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                {canDelete && (
                                  <DropdownMenuItem 
                                    onClick={() => handleDelete(contact.id)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash size={16} className="mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredContacts.length === 0 && (
              <div className="text-center py-12">
                <Users size={48} weight="duotone" className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No contacts found</p>
                {canCreate && (
                  <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
                    <Plus size={18} className="mr-2" />
                    Add Your First Contact
                  </Button>
                )}
              </div>
            )}
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information</DialogDescription>
          </DialogHeader>
          {renderContactForm(handleEdit, true)}
        </DialogContent>
      </Dialog>

      {/* Detail View Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                {selectedContact?.contact_type === 'supplier' ? (
                  <Storefront size={20} className="text-primary" weight="duotone" />
                ) : (
                  <Users size={20} className="text-primary" weight="duotone" />
                )}
              </div>
              {selectedContact?.company_name || selectedContact?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedContact?.contact_type === 'customer' ? 'Customer' : selectedContact?.contact_type === 'supplier' ? 'Supplier' : 'Customer & Supplier'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedContact && (
            <div className="space-y-6">
              {/* Company Info */}
              {selectedContact.company_name && (
                <div className="flex items-start gap-3">
                  <Buildings size={18} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Company</p>
                    <p>{selectedContact.company_name}</p>
                  </div>
                </div>
              )}

              {/* Contact Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                {selectedContact.email && (
                  <div className="flex items-start gap-3">
                    <EnvelopeSimple size={18} className="text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Email</p>
                      <a href={`mailto:${selectedContact.email}`} className="text-primary hover:underline">
                        {selectedContact.email}
                      </a>
                    </div>
                  </div>
                )}
                
                {selectedContact.phone && (
                  <div className="flex items-start gap-3">
                    <Phone size={18} className="text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Phone</p>
                      <a href={`tel:${selectedContact.phone}`} className="text-primary hover:underline">
                        {selectedContact.phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Address */}
              {selectedContact.address && (
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Address</p>
                    <p className="whitespace-pre-wrap">{selectedContact.address}</p>
                    {selectedContact.country && (
                      <p className="text-muted-foreground">{COUNTRIES.find(c => c.code === selectedContact.country)?.name || selectedContact.country}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Tax ID */}
              {selectedContact.tax_id && (
                <div className="flex items-start gap-3">
                  <IdentificationCard size={18} className="text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Tax ID / TRN</p>
                    <p className="font-mono">{selectedContact.tax_id}</p>
                  </div>
                </div>
              )}

              {/* Transaction Summary */}
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Transaction Summary</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const kind = (selectedContact.contact_type === 'supplier') ? 'supplier' : 'customer';
                      navigate(`/dashboard/accounts/statements?tab=${kind}&id=${selectedContact.id}`);
                    }}
                    data-testid={`open-soa-${selectedContact.id}`}
                  >
                    Open Statement of Account
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Invoices</p>
                    <p className="text-lg font-semibold">{selectedContact.total_invoices || 0}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Total Amount</p>
                    <p className="text-lg font-semibold">{formatCurrency(selectedContact.total_amount || 0)}</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedContact.notes && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">{selectedContact.notes}</p>
                </div>
              )}

              {/* Currency */}
              <div className="text-sm text-muted-foreground">
                Default Currency: <Badge variant="outline">{selectedContact.currency || companyCurrency}</Badge>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>Close</Button>
            {canEdit && (
              <Button onClick={() => { setIsDetailOpen(false); openEditDialog(selectedContact); }}>
                <PencilSimple size={16} className="mr-2" />
                Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
